import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Leaf, DollarSign, FileText, User, Mail, MessageSquare, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfferDetail = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOffer();
  }, [id]);

  const fetchOffer = async () => {
    try {
      const response = await axios.get(`${API}/offers/${id}`);
      setOffer(response.data);
    } catch (error) {
      toast.error('Falha ao carregar oferta');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleExpressInterest = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Por favor, faça login para expressar interesse');
      navigate('/auth');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/offers/${id}/interest`, 
        { message },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success('Interesse expressado com sucesso!');
      setMessage('');
      fetchOffer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao expressar interesse');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-zinc-400">Loading offer...</div>
      </div>
    );
  }

  if (!offer) return null;

  const isOwner = user?.id === offer.seller_id;
  const hasExpressedInterest = offer.interested_users?.some(i => i.user_id === user?.id);

  return (
    <div className="min-h-screen py-12 px-6 md:px-12 lg:px-24">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div
                className="h-80 rounded-sm overflow-hidden"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1560409065-edc62644e72d?crop=entropy&cs=srgb&fm=jpg&q=85)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'grayscale(30%)'
                }}
              />

              <Card className="bg-card border-border rounded-sm" data-testid="offer-details-card">
                <CardContent className="p-8 space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                        {offer.biome}
                      </Badge>
                      <Badge className="rounded-full px-3 py-0.5 text-xs font-mono uppercase tracking-wider bg-zinc-800 text-zinc-300 border border-zinc-700">
                        {offer.status}
                      </Badge>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                      {offer.description}
                    </h1>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <User className="w-4 h-4" />
                      <span>Listada por <span className="text-foreground font-medium">{offer.seller_name}</span></span>
                      <span className="mx-2">•</span>
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(offer.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="border-t border-border pt-6">
                    <h3 className="text-lg font-heading font-bold mb-4">Informações da Propriedade</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-secondary p-4 rounded-sm">
                        <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
                          <MapPin className="w-4 h-4" strokeWidth={1.5} />
                          Área Total
                        </div>
                        <div className="text-2xl font-mono font-bold">{offer.total_area.toLocaleString()} ha</div>
                      </div>
                      <div className="bg-secondary p-4 rounded-sm">
                        <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
                          <Leaf className="w-4 h-4" strokeWidth={1.5} />
                          Área Florestal
                        </div>
                        <div className="text-2xl font-mono font-bold">{offer.forest_area.toLocaleString()} ha</div>
                      </div>
                    </div>
                  </div>

                  {offer.documents && offer.documents.length > 0 && (
                    <div className="border-t border-border pt-6">
                      <h3 className="text-lg font-heading font-bold mb-4">Documentação</h3>
                      <div className="space-y-2">
                        {offer.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-secondary p-3 rounded-sm">
                            <FileText className="w-5 h-5 text-primary" />
                            <span className="text-sm">Documento {idx + 1}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {isOwner && offer.interested_users && offer.interested_users.length > 0 && (
                <Card className="bg-card border-border rounded-sm" data-testid="interested-users-card">
                  <CardContent className="p-8">
                    <h3 className="text-lg font-heading font-bold mb-4">
                      Interessados ({offer.interested_users.length})
                    </h3>
                    <div className="space-y-4">
                      {offer.interested_users.map((interest) => (
                        <div key={interest.id} className="bg-secondary p-4 rounded-sm" data-testid={`interest-${interest.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium">{interest.user_name}</div>
                              <div className="flex items-center gap-2 text-sm text-zinc-400">
                                <Mail className="w-3 h-3" />
                                {interest.user_email}
                              </div>
                            </div>
                            <span className="text-xs text-zinc-500">
                              {new Date(interest.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {interest.message && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <div className="flex items-start gap-2 text-sm text-zinc-300">
                                <MessageSquare className="w-4 h-4 mt-0.5 text-zinc-500" />
                                <p>{interest.message}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              <Card className="bg-card border-border rounded-sm sticky top-24" data-testid="purchase-card">
                <CardContent className="p-8 space-y-6">
                  <div>
                    <div className="text-zinc-400 text-sm mb-2">Créditos de Carbono Disponíveis</div>
                    <div className="text-4xl font-mono font-bold text-primary mb-1">
                      {offer.carbon_credits.toLocaleString()}
                    </div>
                    <div className="text-zinc-500 text-sm">tCO₂ equivalente</div>
                  </div>

                  <div className="border-t border-border pt-6">
                    <div className="text-zinc-400 text-sm mb-2">Preço Total</div>
                    <div className="text-3xl font-mono font-bold">${offer.price.toLocaleString()}</div>
                    <div className="text-zinc-500 text-sm mt-1">
                      ${(offer.price / offer.carbon_credits).toFixed(2)} por tCO₂
                    </div>
                  </div>

                  {!isOwner && (
                    <div className="border-t border-border pt-6">
                      {hasExpressedInterest ? (
                        <div className="text-center py-4">
                          <div className="text-primary font-medium mb-2">Interesse Manifestado</div>
                          <p className="text-sm text-zinc-400">
                            O vendedor entrará em contato com você diretamente
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleExpressInterest} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="message">Mensagem (opcional)</Label>
                            <Textarea
                              id="message"
                              placeholder="Adicione uma mensagem para o vendedor..."
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              rows={3}
                              className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm"
                              data-testid="interest-message-input"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-primary text-black hover:bg-[#B3E600] hover:shadow-[0_0_20px_rgba(204,255,0,0.3)] rounded-sm font-medium tracking-wide transition-all duration-200 h-12"
                            data-testid="express-interest-button"
                          >
                            {submitting ? 'Sending...' : 'Express Interest'}
                          </Button>
                          <p className="text-xs text-zinc-500 text-center">
                            Pagamento e assinatura de contrato tratados externamente
                          </p>
                        </form>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OfferDetail;
